import { storeRef } from "@/components/providers/StoreProvider"
import { flightActions } from "@/lib/redux/features/flight/reduce"
import { thunks } from "@/lib/redux/thunks"
import dayjs from "dayjs"
import { getQueryStringSearchFlight } from "../string/getQueryStringSearchFlight"
import { getMinFlights } from "./getMinFlights"
import { checkIsValidInputFlights } from "./checkIsValidInputFlights"
import { toast } from "react-toastify"
import { bookingActions } from "@/lib/redux/features/booking/reduce"
import { getValidDayjs } from "../safeParse/getValidDayjs"
import { getAirlinesCanBeExcuted } from "./getAirlinesCanBeExcuted"
import { controllers } from "@/lib/services"
import { ensureFlatFareConfigLoaded } from "./ensureFlatFareConfigLoaded"
import { track } from "@/lib/tracking"
import { consumeSkyscannerTimeoutBypass } from "@/utils/booking/dayviewAttribution"

export let intervalSessionSearch: any = null
let durationSessionSearch = 900
let startedSearchAt: any = null
// DOP-925: dedupe flight_search (submit + result-mount cùng gọi hàm này)
let lastTrackedSearch = { key: '', at: 0 }

type GetFlightsAllAirlinesParams = { 
  onStart?: ({ newSearchString }: { newSearchString: string }) => void
}

export const getFlightsAllAirlines = async (
  { 
    onStart = () => { }
  }: GetFlightsAllAirlinesParams
) => {
  try {
    const flightState = storeRef.current?.getState().flight

    const missingAirport = (flightState?.inputFlights || []).some(
      (input) => !input.isReturn && (!input.fromAirport?.code || !input.toAirport?.code),
    )
    if (missingAirport) {
      const first = (flightState?.inputFlights || []).find((input) => !input.isReturn)
      if (!first?.fromAirport?.code) {
        toast.error("Vui lòng chọn điểm đi")
      } else {
        toast.error("Vui lòng chọn điểm đến")
      }
      return
    }

    // RESET PROGRESSING
    if (controllers.search_flights) {
      controllers.search_flights.abort("New request")
      controllers.search_flights = new AbortController()
    }
    startedSearchAt = dayjs().format()
    if (intervalSessionSearch) {
      clearInterval(intervalSessionSearch)
    }
    intervalSessionSearch = setInterval(() => {
      const restDuration = durationSessionSearch - dayjs().diff(getValidDayjs(startedSearchAt), 'second')
      if (restDuration <= 0) {
        if (consumeSkyscannerTimeoutBypass('search')) {
          // Temporary hotfix: stale/missing startedSearchAt false-fires this popup right when a
          // NEW Skyscanner redirect lands — treat it as a fresh search session instead. Only
          // bypasses once per redirect id: a real expiry later in this same redirect's session
          // still shows the popup like any other visitor.
          startedSearchAt = dayjs().format()
          storeRef.current?.dispatch(flightActions.setDurationSession(durationSessionSearch))
          return
        }
        storeRef?.current?.dispatch(flightActions.setModalTimeOut(true))
        clearInterval(intervalSessionSearch)
      }
    }, 1000)

    storeRef.current?.dispatch(flightActions.setDurationSession(durationSessionSearch))
    storeRef.current?.dispatch(flightActions.resetResultsFlight())
    storeRef.current?.dispatch(flightActions.resetLoadFlightsProgressing())
    storeRef.current?.dispatch(flightActions.filterFlights({ type: 'class', value: [] }))

    if (!checkIsValidInputFlights()) {
      toast("Bạn đã nhập ngày đi/ngày về hành trình không hợp lệ. Vui lòng kiểm tra lại!", {
        type: 'warning'
      })
      return
    }

    // Điều hướng ngay lập tức, không chờ flat fare config (chỉ dùng để hiển thị
    // giá khuyến mãi VJ, không cần cho việc chuyển trang) — tránh đứng hình
    // giữa lúc bấm nút và lúc trang result bắt đầu chuyển/search.
    const newSearchString = `?${getQueryStringSearchFlight()}`
    localStorage.setItem("search_string_flights", newSearchString)
    await onStart({
      newSearchString
    })

    ensureFlatFareConfigLoaded()

    storeRef.current?.dispatch(flightActions.selectFlightInput({ flightInputIndex: 0 }))

    const inputFlights = (flightState?.inputFlights || []).map((input) => {
      let dForm: any = input.dateFrom
      let dTo: any = input.dateTo

      if (!dayjs.isDayjs(dForm)) {
        dForm = dayjs(dForm)
        if (!dForm.isValid()) {
          dForm = dayjs()
        }
      }

      if (!dayjs.isDayjs(dTo)) {
        dTo = dayjs(dTo)
        if (!dTo.isValid()) {
          dTo = dayjs()
        }
      }
      dTo = dTo.format("YYYY-MM-DD")
      dForm = dForm.format("YYYY-MM-DD")

      return {
        ...input,
        dateFrom: dForm,
        dateTo: dTo
      }
    })

    // Drop all previously chosen legs (e.g. return leg left over after RT → OW)
    storeRef.current?.dispatch(bookingActions.resetFlightToBuy())

    const searchedAt = Date.now()
    const currentlyFlights = inputFlights.reduce((obj, input) => {
      obj[`${input.fromAirport?.code}${input.toAirport?.code}`] = {
        from: input.fromAirport,
        to: input.toAirport,
        dateFrom: input.dateFrom,
        dateTo: input.dateTo,
        cabinClass: flightState?.cabinClass,
        searchedAt,
      }
      return obj
    }, {} as any)


    localStorage.setItem('currently_flights', JSON.stringify(currentlyFlights))
    storeRef.current?.dispatch(flightActions.onUpdateCurrentlyFlight({
      currentlyFlights: {
        ...flightState?.currentlyFlights,
        ...currentlyFlights
      }
    }))

    const airlines = flightState ? getAirlinesCanBeExcuted(flightState) : []
    const isMultiCities = flightState?.isMultiCities

    const flights = inputFlights.reduce((arr, inputFlight) => {
      let dayjsValue: any = inputFlight.dateFrom

      const departmentFlight = {
        startPoint: inputFlight.fromAirport?.code,
        endPoint: inputFlight.toAirport?.code,
        departDate: dayjsValue,
        airline: '',
      }
      arr.push(departmentFlight)
      return arr
    }, [] as any[])

    const isDomestic = !flights.some(flight => {
      const startAirport = flightState?.get_airports.data[flight.startPoint]
      const endAirport = flightState?.get_airports.data[flight.endPoint]
      return startAirport?.isDomestic === '0' || endAirport?.isDomestic === '0'
    })

    const defaultBody = {
      TYPE: 'JSON',
      adt: flightState?.inputAdult || 0,
      chd: flightState?.inputChildren || 0,
      inf: flightState?.inputInfant || 0,
      flights,
      itinerary: flightState?.checkedItinerary ? 1 : isMultiCities ? 2 : 0,
      currency: "VND",
      language: "VI",
      flightType: isDomestic ? "DOMESTIC" : "INTERNATIONAL",
      viewMode: "1"
    }

    // DOP-925: tracking flight_search — gắn tại đây để cover mọi đường vào
    // (desktop submit, mobile form, re-search khi hết session). Dedupe theo bộ
    // tham số trong vòng đời search session (15') vì 1 lượt search của user có
    // thể gọi hàm này nhiều lần (submit form + result mount + effect re-run
    // theo i18n ở FlightsContainer). Đổi tham số → key đổi → track bình thường.
    const searchTrackProperties = {
      origin: inputFlights[0]?.fromAirport?.code,
      destination: inputFlights[0]?.toAirport?.code,
      departure_date: inputFlights[0]?.dateFrom,
      ...(flightState?.checkedItinerary ? { return_date: inputFlights[0]?.dateTo } : {}),
      adult: flightState?.inputAdult || 0,
      child: flightState?.inputChildren || 0,
      infant: flightState?.inputInfant || 0,
      flight_type: isDomestic ? 'DOMESTIC' : 'INTERNATIONAL',
      ...(isMultiCities ? {
        is_multi_cities: true,
        routes: inputFlights.map(input => `${input.fromAirport?.code}-${input.toAirport?.code}:${input.dateFrom}`)
      } : {}),
    }
    const searchTrackKey = JSON.stringify(searchTrackProperties)
    if (searchTrackKey !== lastTrackedSearch.key || Date.now() - lastTrackedSearch.at > durationSessionSearch * 1000) {
      lastTrackedSearch = { key: searchTrackKey, at: Date.now() }
      track('flight_search', searchTrackProperties)
    }

    for (let i = 0; i < airlines.length; i++) {
      const airline = airlines[i];
      const body = {
        ...defaultBody,
        flights: defaultBody.flights.map((flight) => {
          return {
            ...flight,
            airline: airline.code,
          }
        })
      }
      storeRef.current?.dispatch(thunks.search_flights({
        body: body
      })).unwrap().catch(() => {
        // Lỗi (kể cả request bị abort khi search lại) đã được reducer rejected xử lý;
        // nuốt ở đây để không thành unhandledRejection.
      }).finally(() => {
        storeRef.current?.dispatch(flightActions.updateLoadFlightsProgressing())
      })
    }
    getMinFlights(inputFlights[0]?.dateFrom)
    getMinFlights()
    // !isInit && getFareRules({ lang })
  } catch (error) {
    console.error('getFlightsAllAirlines', error)
  }
}
